/**
 * The lobby's derived state: what the screen says, and what the buttons are
 * allowed to do, as pure functions of the connection's phase.
 *
 * Separated from `duel-connection.ts` for the reason the campaign lane's
 * `resolveFlyTarget` was: this is the part with branches, so it is the part
 * worth testing without a socket in the room.
 */

import type { AircraftKey } from "@red-opus/engine";
import type { BuildVerdict, DuelPhase, SignalRole } from "@red-opus/net";
import { duelBanner, type DuelOutcome } from "./duel-outcome.ts";

export interface DuelStatus {
  readonly phase: DuelPhase;
  readonly role: SignalRole | null;
  readonly room: string;
  readonly outcome: DuelOutcome;
  /** What the other player picked, once they have said. */
  readonly opponentAircraft: AircraftKey | null;
  /** True while the simulation is held up waiting for the opponent's stick. */
  readonly waiting: boolean;
  /** Whether the opponent has been confirmed to run this same simulation (`build-gate.ts`). */
  readonly build: BuildVerdict;
  /** Set when the connection failed for a reason worth printing. */
  readonly error: string | null;
}

/** One line telling the player what the connection is doing, in the game's voice. */
export function lobbyNote(status: DuelStatus): string {
  if (status.error) return status.error;
  switch (status.phase) {
    case "idle":
      return "Not connected.";
    case "signaling":
      return status.role === "host"
        ? "Room open — send the code to your opponent and wait for him to arrive."
        : "Looking for the room…";
    case "connecting":
      return "Both here. Negotiating a direct connection…";
    case "synced":
      if (status.build === "unconfirmed") return "Connected. Checking that he is flying the same build…";
      return status.opponentAircraft ? "Connected. Pick your aeroplane." : "Connected. Waiting for his choice…";
    case "running":
      return "In the air.";
    case "ended":
      return duelBanner(status.outcome) ?? "Duel over.";
    case "desynced":
      return "The two simulations diverged. Duel ended.";
    case "mismatched":
      // The connection sets `error` to `buildMismatchNote`, which carries both
      // stamps and is returned above. This is the line if it somehow did not.
      return "Your opponent is flying a different build. Reload the page, both of you, and try again.";
    case "disconnected":
      return "Your opponent's connection went away.";
    default:
      return "";
  }
}

/**
 * Only the host launches, only once both aeroplanes are known, only from the
 * lobby — and only once the opponent's build has been *confirmed*. The last
 * clause is the button half of the build gate: `hello` crosses on channel-open
 * and lands long before a human can click, so it costs a matching pair
 * nothing, and an unconfirmed peer is one whose desync would otherwise arrive
 * in place of a message.
 */
export function canLaunch(status: DuelStatus): boolean {
  return (
    status.role === "host" &&
    status.phase === "synced" &&
    status.build === "same" &&
    status.opponentAircraft !== null
  );
}

/** The guest has no launch button; it says so rather than showing a dead one. */
export function launchHint(status: DuelStatus): string {
  if (status.role !== "host") return "The host starts the duel.";
  if (status.build !== "same") return "Checking that your opponent is flying the same build.";
  if (status.opponentAircraft === null) return "Waiting for your opponent to pick an aeroplane.";
  return "Both aeroplanes picked — start when ready.";
}

/** Whether the lobby's aeroplane picker is still live. */
export function canChooseAircraft(status: DuelStatus): boolean {
  return status.phase === "idle" || status.phase === "signaling" || status.phase === "connecting" || status.phase === "synced";
}
